
import { useState, useCallback } from 'react';
import { debugLogger } from '@/utils/debugLogger';
import { getCountryCode, getLanguageFromCountry, detectBrowserLanguage } from '@/utils/countryLanguageMapping';
import { supabase } from '@/integrations/supabase/client';
import { GoogleGeoLocationService } from '@/lib/geolocation/GoogleGeoLocationService';

export interface LocationData {
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  stateCode?: string;    // ISO code (ex: "SP", "CA")
  country?: string;
  countryCode?: string;  // ISO code (ex: "BR", "US")
  language?: string;
  accuracy?: number;
  source: 'gps' | 'google' | 'ip_api' | 'ipinfo' | 'geoip';
}

interface GeolocationCallbacks {
  onLocationObtained: (location: LocationData) => void;
  onPermissionDenied: () => void;
  onError: (error: string) => void;
}

export const useEnhancedGeolocation = () => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hasPermissionDenied, setHasPermissionDenied] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  // ✅ Adicionar persistência em localStorage para locationObtained
  const [locationObtained, setLocationObtained] = useState(() => {
    try {
      const stored = localStorage.getItem('temp_location_obtained');
      if (stored) {
        const { timestamp, obtained } = JSON.parse(stored);
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        if (obtained && timestamp > fiveMinutesAgo) {
          debugLogger.debug('useEnhancedGeolocation: Restored locationObtained from localStorage');
          return true;
        }
      }
    } catch (error) {
      debugLogger.warn('Failed to load location state from localStorage');
    }
    return false;
  });
  
  const [isUsingGeoIP, setIsUsingGeoIP] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState<string>('');
  const [geoIPProgress, setGeoIPProgress] = useState<string>('');
  const [isBlockedByPolicy, setIsBlockedByPolicy] = useState(false);

  // ✅ Wrapper para salvar em localStorage ao atualizar locationObtained
  const setLocationObtainedWithPersistence = useCallback((value: boolean) => {
    setLocationObtained(value);
    if (value) {
      localStorage.setItem('temp_location_obtained', JSON.stringify({
        obtained: true,
        timestamp: Date.now()
      }));
      debugLogger.debug('useEnhancedGeolocation: Saved locationObtained to localStorage');
    } else {
      localStorage.removeItem('temp_location_obtained');
    }
  }, []);

  const reset = useCallback(() => {
    debugLogger.debug('useEnhancedGeolocation: Resetting state');
    setIsGettingLocation(false);
    setHasPermissionDenied(false);
    setLocationError(null);
    setLocationObtainedWithPersistence(false);
    setIsUsingGeoIP(false);
    setCurrentAttempt('');
    setGeoIPProgress('');
    setIsBlockedByPolicy(false);
  }, [setLocationObtainedWithPersistence]);

  // Função para verificar se geolocalização está bloqueada por Permissions Policy
  const checkGeolocationPermissionsPolicy = async (): Promise<boolean> => {
    // Verificar se a API de Permissions está disponível
    if ('permissions' in navigator && 'query' in navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        if (result.state === 'denied') {
          debugLogger.debug('geolocation', 'Geolocation blocked by browser permissions - fallback available');
          return false;
        }
      } catch (error) {
        // Navegador não suporta query para geolocation - não é um erro crítico
        debugLogger.debug('geolocation', 'Permissions API does not support geolocation query');
      }
    }
    
    // Verificar se navigator.geolocation existe
    if (!navigator.geolocation) {
      debugLogger.debug('geolocation', 'navigator.geolocation not available - fallback available', {
        userAgent: navigator.userAgent,
        hostname: window.location.hostname,
        isInIframe: window.self !== window.top
      });
      return false;
    }
    
    return true;
  };

  // Função para tentar geolocalização via GPS
  const tryGPSLocation = async (): Promise<LocationData> => {
    // Verificar Permissions Policy ANTES de tentar usar GPS
    const isAllowed = await checkGeolocationPermissionsPolicy();
    
    if (!isAllowed) {
      setIsBlockedByPolicy(true);
      debugLogger.debug('geolocation', 'Blocked by Permissions Policy - using IP fallback', {
        userAgent: navigator.userAgent,
        hostname: window.location.hostname,
        isInIframe: window.self !== window.top
      });
      throw new Error('BLOCKED_BY_POLICY');
    }
    return new Promise((resolve, reject) => {
      setCurrentAttempt('Obtendo localização precisa via GPS...');
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude, accuracy } = position.coords;
            debugLogger.debug('GPS location obtained:', { latitude, longitude, accuracy });
            
      // Usar nossa edge function do Google Geocoding para converter coordenadas em endereço
      const address = await reverseGeocodeGoogle(latitude, longitude);
      
      debugLogger.debug('Address resolved from GPS coordinates:', address);

      resolve({
        latitude,
        longitude,
        accuracy,
        city: address.city,
        state: address.state,
        stateCode: address.stateCode,
        country: address.country,
        countryCode: address.countryCode,
        language: address.language,
        source: 'gps' as const
      });
          } catch (error) {
            debugLogger.error('Error in GPS reverse geocoding:', error);
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              source: 'gps' as const
            });
          }
        },
      (error) => {
        debugLogger.info('geolocation', 'GPS permission check:', { code: error.code, message: error.message || 'User action required' });
        
        // Verificar código de erro diretamente (mais robusto)
        if (error.code === 1) { // GeolocationPositionError.PERMISSION_DENIED = 1
          debugLogger.info('geolocation', 'GPS permission denied - using IP fallback');
          reject(new Error('PERMISSION_DENIED'));
        } else if (error.code === 2) { // POSITION_UNAVAILABLE = 2
          debugLogger.warn('GPS position unavailable');
          reject(new Error('POSITION_UNAVAILABLE'));
        } else if (error.code === 3) { // TIMEOUT = 3
          debugLogger.warn('GPS request timeout');
          reject(new Error('TIMEOUT'));
        } else {
          debugLogger.error('Unknown GPS error:', error);
          reject(new Error(`Erro GPS: ${error.message || 'Erro desconhecido'}`));
        }
      },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000 // 5 minutos
        }
      );
    });
  };

  // Helper function for fetch with timeout
  const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs: number = 5000): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  // Função para geocoding reverso via nossa edge function do Google
  const reverseGeocodeGoogle = async (lat: number, lng: number) => {
    try {
      setCurrentAttempt('Convertendo coordenadas via Google...');
      
      // Usar supabase.functions.invoke com body JSON (método POST)
      const { data, error } = await supabase.functions.invoke('google-geocoding', {
        body: {
          lat: lat.toString(),
          lng: lng.toString()
        }
      });
      
      if (error) {
        throw new Error(`Google Geocoding API failed: ${error.message}`);
      }
      
      debugLogger.debug('Google geocoding response:', data);
      
      if (data && (data.city || data.stateCode || data.countryCode)) {
        const language = data.language || getLanguageFromCountry(data.countryCode) || detectBrowserLanguage();
        
        // Bug fix: Retornar state (nome completo) e stateCode (código) consistentemente
        return { 
          city: data.city, 
          state: data.state || data.stateCode, // Nome completo se disponível
          stateCode: data.stateCode,            // Código separado
          country: data.country,
          countryCode: data.countryCode,
          language 
        };
      }
    } catch (error) {
      if ((error as any).name === 'AbortError') {
        debugLogger.error('Google reverse geocoding timeout after 10s');
      } else {
        debugLogger.error('Google reverse geocoding failed:', error);
      }
      
      // Fallback para OpenStreetMap Nominatim com timeout
      try {
        setCurrentAttempt('Tentando geocoding via OpenStreetMap...');
        
        const response = await fetchWithTimeout(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=pt-BR`,
          {},
          5000 // 5 second timeout
        );
        
        if (response.ok) {
          const data = await response.json();
          debugLogger.debug('OpenStreetMap geocoding response:', data);
          
          if (data.address) {
            const countryCode = getCountryCode(data.address.country);
            const language = getLanguageFromCountry(countryCode) || detectBrowserLanguage();
            
            // Use state name directly - stateCode will be resolved by StateSelect component
            const stateName = data.address.state;
            
            return {
              city: data.address.city || data.address.town || data.address.village,
              state: stateName,
              stateCode: stateName, // Will be resolved to proper code by UI
              country: data.address.country,
              countryCode,
              language
            };
          }
        }
      } catch (osmError) {
        debugLogger.error('OpenStreetMap fallback failed:', osmError);
      }
    }
    
    return {};
  };

  // Função auxiliar para tentar Google Geolocation API (IP-based fallback)
  const tryGoogleGeolocationFallback = async (): Promise<LocationData | null> => {
    setIsUsingGeoIP(true);
    setCurrentAttempt('Obtendo localização aproximada via IP...');
    setGeoIPProgress('Conectando à Google Geolocation API...');

    try {
      const googleResult = await GoogleGeoLocationService.getLocation();

      debugLogger.debug('geolocation', 'Google Geolocation API result:', {
        success: googleResult.success,
        source: googleResult.source,
        hasData: !!googleResult.data,
        city: googleResult.data?.city,
        error: googleResult.error
      });

      if (googleResult.success && googleResult.data) {
        debugLogger.debug('Google Geolocation fallback successful:', googleResult.data);
        
        return {
          latitude: googleResult.data.latitude,
          longitude: googleResult.data.longitude,
          city: googleResult.data.city,
          state: googleResult.data.state || googleResult.data.stateCode,
          stateCode: googleResult.data.stateCode,
          country: googleResult.data.country,
          countryCode: googleResult.data.countryCode,
          language: getLanguageFromCountry(googleResult.data.countryCode) || detectBrowserLanguage(),
          source: 'google' as const
        };
      } else {
        debugLogger.warn('geolocation', 'Google Geolocation API failed (will retry):', googleResult.error);
        return null;
      }
    } catch (geoipError) {
      debugLogger.warn('geolocation', 'Google Geolocation fallback exception (will retry):', geoipError);
      return null;
    }
  };

  const getCurrentLocation = useCallback(async (callbacks: GeolocationCallbacks) => {
    setIsGettingLocation(true);
    setLocationError(null);
    setHasPermissionDenied(false);
    setLocationObtained(false);

    try {
      const location = await tryGPSLocation();
      debugLogger.debug('Location obtained successfully:', location);
      
      setLocationObtainedWithPersistence(true);
      debugLogger.debug('State updated: locationObtained = true');
      setIsGettingLocation(false);
      callbacks.onLocationObtained(location);
      return;
    } catch (error: any) {
      debugLogger.debug('geolocation', 'GPS unavailable, trying fallback:', error.message);
      
      // Se GPS foi negado ou falhou, tentar fallback automático via Google API
      if (error.message === 'PERMISSION_DENIED') {
        debugLogger.info('geolocation', 'Using Google Geolocation API (IP-based) as fallback');
        setHasPermissionDenied(true);
      } else if (error.message === 'BLOCKED_BY_POLICY') {
        debugLogger.debug('geolocation', 'Blocked by Permissions Policy, using IP-based fallback');
        setIsBlockedByPolicy(true);
      } else {
        debugLogger.warn('GPS failed, trying Google Geolocation API fallback (IP-based)');
      }
      
      // Tentar Google Geolocation API como fallback (para PERMISSION_DENIED e outros erros)
      const location = await tryGoogleGeolocationFallback();

      if (location) {
        // ✅ CRÍTICO: TODOS os setState puros agrupados (sem side effects)
        setHasPermissionDenied(false);
        setLocationObtained(true);  // setState puro (sem localStorage)
        setIsGettingLocation(false);
        setCurrentAttempt('Localização obtida via IP');
        
        // Log depois dos setState
        debugLogger.debug('State updated: locationObtained = true, hasPermissionDenied = false');
        
        // ✅ Callback chamado após todos os setState
        callbacks.onLocationObtained(location);
        
        // ✅ Side effect (localStorage) executado POR ÚLTIMO
        localStorage.setItem('temp_location_obtained', JSON.stringify({
          obtained: true,
          timestamp: Date.now()
        }));
        debugLogger.debug('useEnhancedGeolocation: Saved locationObtained to localStorage');
        
        return;
      }

      // Se chegou aqui, AMBOS falharam (GPS + Google API)
      debugLogger.error('geolocation', 'CRITICAL: Both GPS and IP fallback failed - user cannot proceed', {
        gpsError: error.message,
        fallbackResult: 'failed',
        userAgent: navigator.userAgent
      });
      
      setLocationError('Não foi possível obter sua localização. Por favor, tente novamente.');
      setIsGettingLocation(false);
      callbacks.onError('Falha em GPS e fallback via IP');
    }
  }, []);

  return {
    isGettingLocation,
    hasPermissionDenied,
    locationError,
    locationObtained,
    isUsingGeoIP,
    currentAttempt,
    geoIPProgress,
    isBlockedByPolicy,
    getCurrentLocation,
    reset
  };
};
